import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, useAdminState, Erro } from "../../hooks/useAdminApi";

/**
 * PAGAMENTOS — cada transação de venda, com a receita por cima.
 *
 * A "Usuários & Assinaturas" mostra o pagamento POR PESSOA (a gaveta de um usuário). Esta
 * tela é a visão GLOBAL: toda a receita num lugar, filtrável — a pergunta do dono do negócio.
 */

const FONTE = { greenn: "Greenn", app_store: "App Store", play_store: "Play Store" };
// status brutos que a origem manda (Greenn/lojas). Rótulo legível, cor por natureza.
const STATUS = {
  paid: ["Pago", "green"],
  refunded: ["Estornado", "orange"],
  chargedback: ["Chargeback", "red"],
  canceled: ["Cancelado", "gray"],
  unpaid: ["Não pago", "gray"],
  refused: ["Recusado", "red"],
  waiting_payment: ["Aguardando", "gray"],
  pending: ["Pendente", "gray"],
};
const rotuloFonte = (s) => FONTE[s] || s || "—";
const rotuloStatus = (s) => (STATUS[s] ? STATUS[s][0] : s || "—");
const corStatus = (s) => (STATUS[s] ? STATUS[s][1] : "gray");

const brl = (n) =>
  `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CORES = {
  green: "var(--green)",
  orange: "var(--orange)",
  red: "var(--cancel)",
  gray: "var(--text3)",
};

export default function Pagamentos() {
  const { tema, setTema } = useOutletContext();
  const { erro, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState({ search: "", source: "", status: "", dias: "" });
  const [page, setPage] = useState(1);

  const carregar = useCallback(
    () =>
      rodar(async () => {
        const q = { page };
        for (const [k, v] of Object.entries(filtros)) if (v !== "") q[k] = v;
        setDados(await apiGet(API_ROUTES.ADMIN.transactions(q)));
      }),
    [rodar, filtros, page]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const resumo = dados?.resumo;
  const rows = useMemo(() => dados?.rows || [], [dados]);
  const total = dados?.total ?? 0;
  const size = dados?.size ?? 25;
  const totalPaginas = Math.max(1, Math.ceil(total / size));

  const setF = (k, v) => {
    setPage(1);
    setFiltros((f) => ({ ...f, [k]: v }));
  };
  const temFiltro = Object.values(filtros).some((v) => v !== "");

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Pagamentos"
        subtitulo="Toda a receita num lugar. A receita conta só o que foi PAGO — estorno e chargeback aparecem no detalhamento, mas não somam."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        {/* ---------- números por cima ---------- */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
          <div className="adm__card" style={{ padding: "16px 24px", minWidth: 190 }}>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5 }}>
              Receita {temFiltro ? "(no filtro)" : "total"}
            </div>
            <strong style={{ fontSize: 27, color: "var(--green)" }}>{brl(resumo?.receita)}</strong>
            <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 3 }}>
              só transações pagas
            </div>
          </div>
          <div className="adm__card" style={{ padding: "16px 24px", minWidth: 130 }}>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5 }}>Pagamentos</div>
            <strong style={{ fontSize: 27 }}>{resumo?.pagos ?? 0}</strong>
            <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 3 }}>com status pago</div>
          </div>
          <div className="adm__card" style={{ padding: "16px 24px", minWidth: 130 }}>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5 }}>Transações</div>
            <strong style={{ fontSize: 27 }}>{total}</strong>
            <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 3 }}>todos os eventos</div>
          </div>
          <Distribuicao titulo="Por status" itens={resumo?.porStatus?.map((s) => ({ chave: s.status, rotulo: rotuloStatus(s.status), cor: corStatus(s.status), qtd: s.qtd, soma: s.soma }))} />
          <Distribuicao titulo="Por fonte" itens={resumo?.porFonte?.map((s) => ({ chave: s.fonte, rotulo: rotuloFonte(s.fonte), cor: "gray", qtd: s.qtd, soma: s.soma }))} />
        </div>

        {/* ---------- filtros ---------- */}
        <div className="adm__card" style={{ padding: 14, margin: "14px 0", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Campo label="Buscar por email">
            <input className="adm__input" style={{ width: 220 }} value={filtros.search} onChange={(e) => setF("search", e.target.value)} placeholder="email do comprador…" />
          </Campo>
          <Campo label="Fonte">
            <Select valor={filtros.source} onChange={(v) => setF("source", v)} mapa={FONTE} vazio="Todas" />
          </Campo>
          <Campo label="Status">
            <Select valor={filtros.status} onChange={(v) => setF("status", v)} mapa={Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v[0]]))} vazio="Todos" />
          </Campo>
          <Campo label="Período">
            <select className="adm__select" style={{ width: 150 }} value={filtros.dias} onChange={(e) => setF("dias", e.target.value)}>
              <option value="">Desde sempre</option>
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
          </Campo>
          {temFiltro && (
            <button className="adm__btnGhost" onClick={() => { setPage(1); setFiltros({ search: "", source: "", status: "", dias: "" }); }} style={{ marginBottom: 1 }}>
              Limpar filtros
            </button>
          )}
        </div>

        {/* ---------- tabela ---------- */}
        {total === 0 ? (
          <div className="adm__card" style={{ padding: 22 }}>
            <p className="adm__vazio" style={{ margin: 0 }}>
              {temFiltro
                ? "Nenhum pagamento bate esses filtros."
                : "Ainda não há transações registradas. Elas entram aqui quando os webhooks de venda (Greenn/lojas) chegarem."}
            </p>
          </div>
        ) : (
          <div className="adm__card" style={{ overflow: "hidden" }}>
            <table className="adm__tabela">
              <thead>
                <tr>
                  <th>Email</th>
                  <th style={{ width: 100 }}>Fonte</th>
                  <th>Produto</th>
                  <th style={{ width: 120 }}>Valor</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 130 }}>Quando</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500 }}>{t.email || "—"}</td>
                    <td>{rotuloFonte(t.source)}</td>
                    <td style={{ color: "var(--text2)", fontSize: 12.5 }}>{t.productId || "—"}</td>
                    <td style={{ fontWeight: 600 }}>{t.amount == null ? "—" : brl(t.amount)}</td>
                    <td>
                      <span className="adm__chip" style={{ color: CORES[corStatus(t.status)], borderColor: "var(--border)" }}>
                        {rotuloStatus(t.status)}
                      </span>
                    </td>
                    <td style={{ color: "var(--text3)", fontSize: 12 }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString("pt-BR") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPaginas > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 16 }}>
            <button className="adm__btnGhost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Anterior</button>
            <span style={{ fontSize: 13, color: "var(--text2)" }}>Página {page} de {totalPaginas}</span>
            <button className="adm__btnGhost" disabled={page >= totalPaginas} onClick={() => setPage((p) => p + 1)}>Próxima →</button>
          </div>
        )}
      </div>
    </>
  );
}

function Campo({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11.5, color: "var(--text2)", fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function Select({ valor, onChange, mapa, vazio }) {
  return (
    <select className="adm__select" style={{ width: 160 }} value={valor} onChange={(e) => onChange(e.target.value)}>
      <option value="">{vazio}</option>
      {Object.entries(mapa).map(([k, label]) => (
        <option key={k} value={k}>{label}</option>
      ))}
    </select>
  );
}

/** Distribuição com quantidade E valor em R$ (barra proporcional à quantidade). */
function Distribuicao({ titulo, itens }) {
  const lista = itens || [];
  const max = lista.reduce((m, i) => Math.max(m, i.qtd), 0) || 1;
  return (
    <div className="adm__card" style={{ padding: "13px 16px", minWidth: 230, flex: "1 1 230px", maxWidth: 340 }}>
      <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 9, fontWeight: 600 }}>{titulo}</div>
      {!lista.length ? (
        <div style={{ fontSize: 12, color: "var(--text3)" }}>sem dados ainda</div>
      ) : (
        lista.slice(0, 5).map((i) => (
          <div key={i.chave} style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 2, gap: 8 }}>
              <span style={{ color: CORES[i.cor] || "var(--text)" }}>{i.rotulo}</span>
              <span style={{ color: "var(--text2)", whiteSpace: "nowrap" }}>
                <strong>{i.qtd}</strong>
                {i.soma > 0 && <span style={{ color: "var(--text3)" }}> · {brl(i.soma)}</span>}
              </span>
            </div>
            <div style={{ height: 5, borderRadius: 4, background: "var(--elev)" }}>
              <div style={{ height: "100%", width: `${(i.qtd / max) * 100}%`, borderRadius: 4, background: CORES[i.cor] || "var(--red)" }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
